// Angular
import { Observable, ObservableInput, Subject, delay, distinctUntilChanged, filter, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Libraries
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { mergeAll } from 'rxjs/operators';
import { Select } from '@ngxs/store';

// Models
import { CompanyListModel } from '../../../company/models/company-view-model.model';
import { AppCleanup, LocationDto, LookUpResponse, UserModel } from '../../../../shared/models';
import { Job } from '../../models';
import { JobScopeDto } from '../../models/job-scope-dto.model';
import { SplitMergeJobPayload } from '../../models/split-merge-job-payload.model';

// Services
import { CompanyService } from '../../../company/services/company.service';
import { LocationService, NotificationService, UtilityService } from '../../../../shared/services';
import { JobService } from '../../services/job.service';
import { CoreService } from '../../../../shared/services/core.service';

// Store
import { UserAuthState } from '../../../auth/state/user-auth.state';

// Hub
import { JobHub } from '../../services/job.hub';
import { LookupState } from '../../../../shared/state/lookup.state';

@Component({
	selector: 'job-split-merge-dialog',
	templateUrl: './job-split-merge-dialog.component.html',
	styleUrls: ['./job-split-merge-dialog.component.scss'],
})
export class JobSplitMergeDialogComponent extends AppCleanup implements OnInit, AfterViewInit, OnDestroy {
	currentUser: UserModel;
	@Select(UserAuthState.currentUser) currentUser$: Observable<UserModel>;
	@Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
	submitted = false;
	companies: CompanyListModel[] = [];
	form: FormGroup;
	jobScopes: JobScopeDto[] = [];
	locations: LocationDto[];
	allPointsAfterStartDate = false;
	allPointsBeforeEndDate = false;
	mergeTargetStatus$ = new Subject<MergeTargetStatus>();
	toJobDisplayName$: Observable<string>;
	timeDiff$: Observable<string>;
	finalMergeTargetStatusResult$: Observable<MergeTargetStatus>;
	eitherDateInputChanged$: Observable<any>;
	orientation: 'horizontal' | 'vertical';

	@HostListener('window:resize', ['$event'])
	onResize() {
		if (window.innerWidth > 991) {
			this.orientation = 'horizontal';
		} else {
			this.orientation = 'vertical';
		}
	}

	constructor(
		public dialogRef: MatDialogRef<JobSplitMergeDialogComponent>,
		private dialog: MatDialog,
		private companyService: CompanyService,
		private jobService: JobService,
		private locationService: LocationService,
		private formBuilder: FormBuilder,
		private notificationService: NotificationService,
		private jobHub: JobHub,
		private coreService: CoreService,
		@Inject(MAT_DIALOG_DATA)
		public data: { splitType: string; fromJob: Job; startDate: Date; endDate: Date },
		private utilityService: UtilityService
	) {
		super();

		let diffFromEnd = this.data.fromJob.end ? this.data.fromJob.end.getTime() - this.data.endDate.getTime() : null;

		let diffFromStart = this.data?.startDate?.getTime() - this.data?.fromJob?.start?.getTime();

		if (diffFromEnd !== null && diffFromEnd <= diffFromStart && diffFromEnd < 60000) {
			this.allPointsAfterStartDate = true;
		}

		if ((this.data.splitType === 'merge' || !this.allPointsAfterStartDate) && diffFromStart < 60000) {
			this.allPointsBeforeEndDate = true;
		}
	}

	ngOnInit() {
		this.utilityService.simulateWindowResize();

		this.currentUser$.pipe(takeUntil(this.destroy)).subscribe((currentUser) => {
			if (currentUser) {
				this.currentUser = currentUser;
			}
		});

		this.buildForm();
		this.companies = this.companyService.companiesList;

		this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
			if (lookups) {
				this.jobScopes = this.data.fromJob.jobTypeId ? lookups.jobScopes.filter((js) => js.jobTypeId === this.data.fromJob.jobTypeId) : [];
			}
		});

		this.locations = this.locationService.locations;

		if (this.data.splitType === 'merge') {
			this.finalMergeTargetStatusResult$ = this.form.get('toJobId').valueChanges.pipe(
				distinctUntilChanged(),
				switchMap<number, ObservableInput<MergeTargetStatus>>((toJobId) =>
					of<MergeTargetStatus>({
						shouldContinue: !!toJobId && toJobId !== this.jobService.currentJob.id,
						isReady: null,
						toJobId,
						job: null,
						message: !toJobId ? '' : toJobId === this.jobService.currentJob.id ? "Can't merge a job to itself." : `Checking status ...`,
						displayName: '',
					}).pipe(
						tap((state: MergeTargetStatus) => this.mergeTargetStatus$.next(state)),

						// delay instead of the usual debounceTime for valueChanges,
						// since everything's inside of one switchMap
						delay(1000),

						filter((state: MergeTargetStatus) => state.shouldContinue),
						mergeMap((state: MergeTargetStatus) =>
							this.jobService.tryGetJobAsMergeTarget(state.toJobId).pipe(
								map((job) =>
									job
										? {
												...state,
												job,
												displayName: this.getMergeToJobDisplayName(job),
											}
										: {
												...state,
												shouldContinue: false,
												isReady: false,
												message: 'JobId not valid.',
											}
								)
							)
						),
						tap((state: MergeTargetStatus) => this.mergeTargetStatus$.next(state)),

						filter((state: MergeTargetStatus) => state.shouldContinue),
						mergeMap((state: MergeTargetStatus) =>
							this.jobService.getJobSplitMergeAvailability(state.toJobId).pipe(
								map((isSplitMergeAvailable) =>
									isSplitMergeAvailable
										? {
												...state,
												shouldContinue: false,
												isReady: true,
												message: 'Ready to merge!',
											}
										: {
												...state,
												shouldContinue: false,
												isReady: false,
												message: "Can't merge yet. Target job is still processing. Check back later.",
											}
								)
							)
						),
						tap((state: MergeTargetStatus) => this.mergeTargetStatus$.next(state))
					)
				)
			);
		}

		this.eitherDateInputChanged$ = of(this.form.get('startDate').valueChanges, this.form.get('endDate').valueChanges).pipe(mergeAll());

		this.timeDiff$ = of(of(this.getTimeDiff()), this.eitherDateInputChanged$.pipe(map(() => this.getTimeDiff()))).pipe(mergeAll());

		this.toJobDisplayName$ = of(of(this.getToJobDisplayName()), this.mergeTargetStatus$.pipe(map((state) => state.displayName)), this.form.valueChanges.pipe(map((_) => this.getToJobDisplayName()))).pipe(mergeAll());
	}

	ngAfterViewInit() {}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	submit() {
		let splitJobPayload: SplitMergeJobPayload = {
			fromJobId: this.data.fromJob.id,
			companyId: this.form.controls.companyId?.value,
			wellName: this.form.controls.wellName?.value,
			wellNumber: this.form.controls.wellNumber?.value,
			jobScopeId: this.form.controls.jobScopeId?.value,
			locationId: this.form.controls.locationId?.value,
			utcDateStarted: this.form.controls.startDate.value,
			utcDateEnded: this.form.controls.endDate.value,
			allPointsAfterStartDate: this.allPointsAfterStartDate,
			allPointsBeforeEndDate: this.allPointsBeforeEndDate,
			toJobId: this.form.controls.toJobId?.value,
		};

		this.submitted = true;
		this.jobService.splitOrMerge(splitJobPayload);
		this.jobHub.startSendingData([this.currentUser.username]);
		this.jobService.refreshJob(String(this.data.fromJob.id), true, false, 'splitMerge');
		this.jobService.refreshCurrentJobSplitMergeAvailability();
		this.submitted = false;
		this.dialogRef.close();
	}

	buildForm() {
		if (this.data.splitType === 'split') {
			this.form = this.formBuilder.group({
				companyId: [this.data.fromJob.companyId || null],
				wellName: [this.data.fromJob.wellName || null, [Validators.maxLength(150)]],
				wellNumber: [this.data.fromJob.wellNumber || null, Validators.maxLength(50)],
				jobScopeId: [this.data.fromJob.jobScopeId || null],
				locationId: [this.data.fromJob.location?.id || null],
				startDate: [this.data.startDate, Validators.required],
				endDate: [this.data.endDate, Validators.required],
			});
		} else if (this.data.splitType === 'merge') {
			this.form = this.formBuilder.group({
				toJobId: [null, [Validators.required, Validators.pattern('[0-9]*')]],
				startDate: [this.data.startDate, Validators.required],
				endDate: [this.data.endDate, Validators.required],
			});
		}
	}

	changeAllPointsAfterStartDate() {
		this.allPointsAfterStartDate = !this.allPointsAfterStartDate;

		if (this.allPointsBeforeEndDate && this.data.splitType === 'split') this.allPointsBeforeEndDate = false;
	}

	changeAllPointsBeforeEndDate() {
		this.allPointsBeforeEndDate = !this.allPointsBeforeEndDate;

		if (this.allPointsAfterStartDate && this.data.splitType === 'split') this.allPointsAfterStartDate = false;
	}

	getTimeDiff(): string {
		let endDate = this.allPointsAfterStartDate && this.data.fromJob.end ? this.data.fromJob.end : this.form.controls.endDate.value;
		let startDate = this.allPointsBeforeEndDate && this.data.fromJob.start ? this.data.fromJob.start : this.form.controls.startDate.value;

		let totalSeconds = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);
		// seconds total
		let totalDays = Math.floor(totalSeconds / (60 * 60 * 24));
		totalSeconds %= 60 * 60 * 24;
		let totalHours = Math.floor(totalSeconds / (60 * 60));
		totalSeconds %= 60 * 60;
		let totalMinutes = Math.floor(totalSeconds / 60);
		totalSeconds %= 60;

		let totalTime: string = '';
		if (totalDays > 0) totalTime += `${totalDays}d `;
		if (totalHours > 0) totalTime += `${totalHours}h `;
		if (totalMinutes > 0) totalTime += `${totalMinutes}m `;
		if (totalSeconds > 0) totalTime += `${totalSeconds}s `;

		return totalTime;
	}

	getMergeToJobDisplayName(job: Job): string {
		if (job) {
			const firstTry = job.nameDisplay && job.locationDisplay ? job.nameDisplay + ' - ' + job.locationDisplay : job.nameDisplay || job.locationDisplay;

			if (firstTry) return firstTry;

			return job.name;
		}

		return '';
	}

	getToJobDisplayName(): string {
		if (this.data.splitType === 'split') {
			let toJob: any = {
				company: this.companies.find((c) => c.id === this.form.controls.companyId?.value),
				location: this.locations.find((c) => c.id === this.form.controls.locationId?.value),
				unit: this.data.fromJob.unit,
				wellName: this.form.controls.wellName?.value,
				wellNumber: this.form.controls.wellNumber?.value,
			};

			toJob = new Job(toJob);

			return toJob.nameDisplay || toJob.locationDisplay;
		}

		return '';
	}
}

interface MergeTargetStatus {
	shouldContinue: boolean;
	isReady: boolean | null;
	toJobId: number | null;
	job: Job | null;
	message: string;
	displayName: string;
}
